import { forwardRef, useState } from 'react';

import { ia, mString } from 'lib/helpers/utility';

import Dot from 'components/shared/Dot/Dot';
import Icon from 'components/shared/Icon/Icon';
import Button from 'components/shared/Button/Button';
import Disclosure from 'components/shared/Disclosure/Disclosure';
import Typography from 'components/shared/Typography/Typography';
import {
  usePaymentContext,
  usePaymentContextApi
} from 'components/Payments/NewPayment/PaymentContent/context/PaymentContext';

const Caption = ({ ach }) => {
  return (
    <div className="flex items-center gap-1.5">
      <Typography variant="p" color="neutral-600" weight="medium" size="xs">
        {ach.account_number}
      </Typography>
      <Dot size="xs" color="neutral" />
      <Typography variant="p" color="neutral-600" weight="medium" size="xs">
        {ach.account_type}
      </Typography>
    </div>
  );
};

const SavedACH = forwardRef(({ savedACHs, isFetchingNextPage, hideButton }, ref) => {
  const [error, setError] = useState(null);
  const { values, isCharging } = usePaymentContext();
  const { handlePayment, setFieldValue } = usePaymentContextApi();

  if (!ia(savedACHs)) return null;

  const handlePaySavedACH = async () => {
    if (!values.methodDetail) {
      setError('Select an ACH to pay with');
      return;
    }

    if (error) setError(null);
    const response = await handlePayment();
    if (response && response.code !== 0) {
      setError(response.error || "The payment couldn't be processed. Please try again.");
    }
  };

  const handleToggleDisclosure = (id) => {
    if (error) setError(null);
    setFieldValue('methodDetail', id);
  };

  return (
    <div className="flex flex-col gap-2">
      <div ref={ref} className="max-h-80 overflow-y-auto">
        <div className="flex flex-col gap-2 p-2">
          {error && (
            <Typography variant="p" color="danger-500" size="xs">
              {error}
            </Typography>
          )}
          {savedACHs.map((ach) => {
            const isActive = values.methodDetail === ach.id;
            return (
              <Disclosure
                key={ach.id}
                isOpen={isActive}
                icon={<Icon icon="building-mono" size={18} />}
                onToggle={(open) => handleToggleDisclosure(open ? ach.id : null)}
                disableOpen
                caption={<Caption ach={ach} />}
                bodyClassName="flex-col"
                buttonClassName="py-2.5"
                className="flex-shrink-0"
                customTitle={
                  <Typography variant="label" size="medium">
                    {ach.name}
                  </Typography>
                }
              />
            );
          })}
        </div>
        {isFetchingNextPage && (
          <div className="align-center flex justify-center pb-2">
            <Icon icon="loader-linear" className="animate-spin" color="primary" size={24} />
          </div>
        )}
      </div>
      {!hideButton && (
        <div className="flex justify-end">
          <Button
            className="justify-self-end"
            text={`Pay ${mString(values.amount)}`}
            onClick={handlePaySavedACH}
            size="small"
            isLoading={isCharging}
          />
        </div>
      )}
    </div>
  );
});

export default SavedACH;
